import React from 'react';
import styles from './styles.scss';
import moment from 'moment';
import { DispatchType } from '../../types/rx-actions';

import {
  CaptureScheduleModalActionTypes,
  CaptureScheduleModalState,
  PartialCaptureSchedule,
  Status,
} from '../../types/capture-schedule-modal';

import {
  parseCaptureDurationInSeconds,
  parseCapturesPerSchedule,
  TWENTY_FOUR_HOURS_IN_SECONDS,
} from '../../utils/calculate-capture-schedules';

import { NextButton } from './utils';

import {
  AppBar,
  AppBarContext,
  AppBarSection,
  AppBarTitle,
  Button,
  ButtonGroup,
  DayOfWeekPicker,
  InputBox,
  ListView,
  ListViewColumn,
} from '@densityco/ui';

// Determine a list of all times that are shown in the start time dropdown
let TIME_CHOICES = [];
const startOfUtcDay = moment.utc().startOf('day');
for (
  let time = startOfUtcDay.clone();
  time.unix() < startOfUtcDay.unix() + TWENTY_FOUR_HOURS_IN_SECONDS;
  time.add(15, 'minutes')
) {
  TIME_CHOICES.push({
    id: time.format('HH:mm:ss'),
    label: time.format('hh:mm a'),
  });
}

function formatDuration(duration: moment.Duration): string {
  return moment
    .utc()
    .startOf('day')
    .add(duration)
    .format('hh:mm a');
}

type CaptureSchedulesPage = {
  state: CaptureScheduleModalState;
  dispatch: DispatchType;
  onDismiss: () => void;
};

const CaptureSchedulesPage: React.FunctionComponent<CaptureSchedulesPage> = props => {
  const { state, dispatch, onDismiss } = props;

  const captureDuration = parseCaptureDurationInSeconds(state);
  const capturesPerSchedule = parseCapturesPerSchedule(state);

  const scheduleEndTimes = state.schedules.map(schedule => {
    return moment
      .duration(schedule.time)
      .add(captureDuration * capturesPerSchedule, 'seconds');
  });

  return (
    <div className={styles.modalWrapper}>
      <div className={styles.captureScheduleSettings}>
        <AppBarContext.Provider value="CARD_HEADER">
          <AppBar>
            <AppBarTitle>Add a Schedule</AppBarTitle>
          </AppBar>
        </AppBarContext.Provider>

        <div className={styles.addScheduleForm}>
          <div className={styles.formInput}>
            <label>
              Start Time
              <span className={styles.required}>* Required</span>
            </label>
            <InputBox
              type="select"
              placeholder="Pick a time"
              width="100%"
              value={state.workingScheduleCopy.time}
              choices={TIME_CHOICES}
              menuMaxHeight={200}
              onChange={item => {
                dispatch({
                  type:
                    CaptureScheduleModalActionTypes.CAPTURE_SCHEDULE_MODAL_WORKING_SCHEDULE_CHANGE_TIME,
                  time: item.id,
                });
              }}
              id="time"
            />
          </div>
          <div className={styles.formInput}>
            <label>
              Days Of Week
              <span className={styles.required}>* Required</span>
            </label>
            <DayOfWeekPicker
              daysOfWeek={state.workingScheduleCopy.daysOfWeek}
              disabled={false}
              onChange={daysOfWeek => {
                dispatch({
                  type:
                    CaptureScheduleModalActionTypes.CAPTURE_SCHEDULE_MODAL_WORKING_SCHEDULE_CHANGE_DAYS_OF_WEEK,
                  daysOfWeek,
                });
              }}
            />
          </div>
          <div className={styles.formInput}>
            <label htmlFor="#event-retries">Number of event retries</label>
            <InputBox
              type="text"
              placeholder="0"
              width="100%"
              value={state.workingScheduleCopy.numberOfEventRetries}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                dispatch({
                  type:
                    CaptureScheduleModalActionTypes.CAPTURE_SCHEDULE_MODAL_WORKING_SCHEDULE_CHANGE_NUMBER_EVENT_RETRIES,
                  numberOfEventRetries: e.target.value,
                });
              }}
              id="event-retries"
            />
          </div>
        </div>
        <AppBarContext.Provider value="BOTTOM_ACTIONS">
          <AppBar>
            <AppBarSection />
            <AppBarSection>
              <ButtonGroup>
                <Button
                  variant="default"
                  onClick={() => {
                    TIME_CHOICES.slice(32, -24).map(startTime => {
                      dispatch({
                        type:
                          CaptureScheduleModalActionTypes.CAPTURE_SCHEDULE_MODAL_QUICK_MF,
                        time: startTime['id'],
                        daysOfWeek: [
                          'Monday',
                          'Tuesday',
                          'Wednesday',
                          'Thursday',
                          'Friday',
                        ],
                        numberOfEventRetries: 10,
                      });
                    });
                  }}
                >
                  Quick Schedule (M-F, 8-6)
                </Button>
                <Button
                  variant="default"
                  onClick={() => {
                    dispatch({
                      type:
                        CaptureScheduleModalActionTypes.CAPTURE_SCHEDULE_MODAL_ADD_CAPTURE_SCHEDULE,
                    });
                  }}
                  disabled={
                    !state.workingScheduleCopy.time ||
                    state.workingScheduleCopy.daysOfWeek.length === 0
                  }
                >
                  Add
                </Button>
              </ButtonGroup>
            </AppBarSection>
          </AppBar>
        </AppBarContext.Provider>
      </div>
      <div className={styles.captureScheduleContainer}>
        <AppBarContext.Provider value="CARD_HEADER">
          <AppBar>
            <AppBarTitle>Schedules (Space Local Time)</AppBarTitle>
          </AppBar>
        </AppBarContext.Provider>
        <div className={styles.captureScheduleList}>
          <ListView padOuterColumns data={state.schedules}>
            <ListViewColumn
              id="Start Time"
              template={(schedule: PartialCaptureSchedule) =>
                formatDuration(moment.duration(schedule.time))
              }
            />
            <ListViewColumn
              id="End Time"
              template={(schedule: PartialCaptureSchedule) =>
                formatDuration(
                  scheduleEndTimes[state.schedules.indexOf(schedule)],
                )
              }
            />
            <ListViewColumn
              id="Days"
              width={225}
              template={(schedule: PartialCaptureSchedule) => (
                <DayOfWeekPicker
                  daysOfWeek={schedule.daysOfWeek}
                  disabled={true}
                />
              )}
            />
            <ListViewColumn
              id="Event retries"
              template={(schedule: PartialCaptureSchedule) =>
                schedule.numberOfEventRetries
              }
            />
            <ListViewColumn
              id="Delete"
              title=" "
              width={100}
              template={(schedule: PartialCaptureSchedule) => (
                <Button
                  variant="underline"
                  type="danger"
                  onClick={() => {
                    dispatch({
                      type:
                        CaptureScheduleModalActionTypes.CAPTURE_SCHEDULE_MODAL_REMOVE_CAPTURE_SCHEDULE,
                      index: state.schedules.indexOf(schedule),
                    });
                  }}
                >
                  Delete
                </Button>
              )}
            />
          </ListView>
        </div>
        <AppBarContext.Provider value="BOTTOM_ACTIONS">
          <AppBar>
            <AppBarSection />
            <AppBarSection>
              <ButtonGroup>
                <Button variant="underline" onClick={() => onDismiss()}>
                  Cancel
                </Button>
                <NextButton
                  page={state.page}
                  disabled={state.schedules.length === 0}
                />
              </ButtonGroup>
            </AppBarSection>
          </AppBar>
        </AppBarContext.Provider>
      </div>
    </div>
  );
};
export default CaptureSchedulesPage;
